import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { X } from 'lucide-react';

import { closeModal } from '@/components/modals/modals.utils';
import { ModalBase } from '@/components/modals/modals-base';
import { Button } from '@/components/ui/button';
import Spinner from '@/components/ui/spinner';
import { getImages } from '@/features/estate-media/api/images';
import { SelectedImage } from '@/features/estate-media/components/selected-image';

export interface MediaLibraryModalProps {
  estateId: string;
  imageId?: string;
  onSelectImage: (id: string) => void;
}

export const MediaLibraryModal = ({ estateId, imageId, onSelectImage }: MediaLibraryModalProps) => {
  const { t } = useTranslation();

  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedImageId, setSelectedImageId] = useState<string | undefined>(imageId);

  const handleSave = () => {
    if (!!onSelectImage && !!selectedImageId) {
      onSelectImage(selectedImageId);
    }
    closeModal();
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getImages(estateId);

        setImages(res.images);
      } finally {
        setLoading(false);
      }
    };

    if (estateId) {
      fetchData();
    }
  }, [estateId]);

  return (
    <ModalBase className="flex min-h-[600px] w-full max-w-[900px] flex-col overflow-hidden rounded-sm p-0">
      <div className="flex h-[60px] items-center justify-between rounded-b-sm bg-text-primary pl-5 pr-6">
        <span className="text-sm font-semibold text-white">{t('mediaLibrary')}</span>
        <X className="cursor-pointer stroke-[4px] text-white" onClick={closeModal} />
      </div>
      <div className="flex flex-1 flex-col p-4">
        {loading ? (
          <div className="flex flex-1 items-center justify-center bg-background">
            <Spinner className="size-12 [&_*]:stroke-primary-dark" />
          </div>
        ) : images.length ? (
          <>
            <div className="grid w-full grid-cols-6 gap-1 max-md:grid-cols-3 max-sm:grid-cols-2">
              {images.map((file) => (
                <div
                  key={file.id}
                  className="cursor-pointer whitespace-normal break-words px-1"
                  onClick={() => setSelectedImageId(file.mediaId)}
                >
                  <SelectedImage
                    fileType={'fileType' in file ? file.fileType : ''}
                    fileName={file.fileName as string}
                    mediaId={file?.mediaId as string}
                    onClick={() => {
                      setSelectedImageId(file.mediaId);
                    }}
                    isSelected={selectedImageId === file.mediaId}
                  />
                </div>
              ))}
            </div>
            <div className="mt-auto flex justify-end">
              <Button className="h-[38px]" disabled={!selectedImageId} onClick={handleSave}>
                {t('save')}
              </Button>
            </div>
          </>
        ) : (
          <div className="flex flex-1 items-center justify-center">
            <span>{t('noContent')}</span>
          </div>
        )}
      </div>
    </ModalBase>
  );
};
